"use client";
import { LocaleLink } from "@i18n/routing";
import { type Config, config } from "@repo/config";
import { plans as creditPlans } from "@repo/config/credits";
import { useAnalytics } from "@analytics/provider/plausible";
import { usePlanData } from "@saas/payments/hooks/plan-data";
import { usePurchases } from "@saas/payments/hooks/purchases";
import {
	useCreateCheckoutLinkMutation,
	useCreateCustomerPortalLinkMutation,
} from "@saas/payments/lib/api";
import type { PlanId } from "@saas/payments/types";
import { useLocaleCurrency } from "@shared/hooks/locale-currency";
import { useRouter } from "@shared/hooks/router";
import { Button } from "@ui/components/button";
import { Tabs, TabsList, TabsTrigger } from "@ui/components/tabs";
import { cn } from "@ui/lib";
import {
	ArrowRightIcon,
	BadgePercentIcon,
	BriefcaseIcon,
	CheckIcon,
	ImageIcon,
	InfinityIcon,
	KeyboardIcon,
	type LucideIcon,
	MessageSquareTextIcon,
	PhoneIcon,
	StarIcon,
	Zap,
} from "lucide-react";
import { Gift as GiftIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import { ChangePlanButton } from "./ChangePlanButton";

const plans = config.payments.plans as Config["payments"]["plans"];

export function PricingTable({
	className,
	userId,
	organizationId,
	activePlanId,
}: {
	className?: string;
	userId?: string;
	organizationId?: string;
	activePlanId?: string;
}) {
	const t = useTranslations();
	const router = useRouter();
	const localeCurrency = useLocaleCurrency();
	const [loading, setLoading] = useState<PlanId | false>(false);
	const [interval, setInterval] = useState<"month" | "year">("year");
	const { activePlan } = usePurchases(organizationId);

	// 获取activeProductId
	const activeProductId = activePlan?.price?.productId;

	// 直接从activePlan获取interval
	const activeInterval = activePlan?.interval as "month" | "year" | null;

	const { planData } = usePlanData();

	const createCheckoutLinkMutation = useCreateCheckoutLinkMutation();
	const createCustomerPortalMutation = useCreateCustomerPortalLinkMutation();
	const { trackEvent } = useAnalytics();

	const onSelectPlan = async (planId: PlanId, productId?: string) => {
		if (!(userId || organizationId)) {
			router.push("/app/subscription");
			return;
		}

		// Validate productId
		if (!productId || productId.trim() === "") {
			toast.error("Invalid plan selected");
			return;
		}

		if (activePlan?.status === "active" && activePlan.purchaseId) {
			setLoading(planId);
			try {
				console.log(
					`Creating customer portal link for purchase: ${activePlan.purchaseId}, product: ${productId}`,
				);

				const data = {
					purchaseId: activePlan.purchaseId,
					redirectUrl: window.location.href,
					context: "change_plan",
					newProductId: productId,
				};

				console.log("Portal creation data:", data);
				const { customerPortalLink } =
					await createCustomerPortalMutation.mutateAsync(data);

				console.log(
					`Redirecting to customer portal: ${customerPortalLink}`,
				);
				window.location.href = customerPortalLink;
			} catch (error) {
				console.error("Failed to create customer portal link:", error);
				toast.error(
					t(
						"settings.billing.createCustomerPortal.notifications.error.title",
					),
				);
			} finally {
				setLoading(false);
			}
			return;
		}

		const plan = plans[planId];
		const price = plan.prices?.find(
			(price) => price.productId === productId,
		);

		if (!price) {
			return;
		}

		setLoading(planId);

		try {
			const { checkoutLink } =
				await createCheckoutLinkMutation.mutateAsync({
					type:
						price.type === "one-time" ? "one-time" : "subscription",
					productId,
					organizationId,
					redirectUrl: window.location.href,
				});

			// Track checkout click event if it's a creem payment link
			if (checkoutLink && checkoutLink.includes("creem")) {
				trackEvent("Checkout Clicked", {
					plan: planId,
					interval: interval,
					productId: productId,
				});
			}

			window.location.href = checkoutLink;
		} catch (error) {
			console.error(error);
			toast.error("Failed to create checkout");
		} finally {
			setLoading(false);
		}
	};

	const filteredPlans = Object.entries(plans).filter(([planId, plan]) => {
		// 不再过滤掉当前激活的计划
		// 如果当前有激活的计划，不显示相同计划
		// if (planId === activePlanId) return false;

		// 如果当前已有付费计划，不显示免费计划
		if (planId === "free" && activePlanId && activePlanId !== "free")
			return false;

		// 检查计划是否有有效的价格和productId
		if (!plan.isEnterprise) {
			const hasValidPrice = plan.prices?.some(
				(price) => price.productId && price.productId.trim() !== "",
			);
			// 免费计划或者有有效价格的计划才显示
			return plan.isFree || hasValidPrice;
		}

		return true;
	});

	const hasSubscriptions = filteredPlans.some(([_, plan]) =>
		plan.prices?.some((price) => price.type === "recurring"),
	);

	// 根据功能内容决定使用哪个图标
	const getFeatureIcon = (feature: string): LucideIcon => {
		// 处理各种语言的图像转文本
		if (
			feature.includes("Image-to-Text") ||
			feature.includes("图像转文本") ||
			feature.includes("图像到文本")
		) {
			return ImageIcon;
		}
		// 处理无限制的文本到提示
		if (
			(feature.includes("Unlimited") || feature.includes("无限制")) &&
			(feature.includes("Text-to-Prompt") ||
				feature.includes("文本到提示") ||
				feature.includes("文本到提示生成"))
		) {
			return KeyboardIcon;
		}
		// 处理任何无限制的功能
		if (feature.includes("Unlimited") || feature.includes("无限制")) {
			return InfinityIcon;
		}
		// 处理文本到提示功能
		if (
			feature.includes("Text-to-Prompt") ||
			feature.includes("文本到提示") ||
			feature.includes("文本到提示生成")
		) {
			return MessageSquareTextIcon;
		}
		// 处理额外积分
		if (feature.includes("Bonus") || feature.includes("额外")) {
			return GiftIcon;
		}
		// 处理商业许可
		if (
			feature.includes("Commercial License") ||
			feature.includes("商业许可")
		) {
			return BriefcaseIcon;
		}
		// 处理Flux相关功能
		if (feature.includes("Flux.1")) {
			return Zap;
		}

		// 默认图标
		return CheckIcon;
	};

	// 处理并更新功能特性，使用credits.ts中的数据
	const getUpdatedFeatures = (
		planId: string,
		features: React.ReactNode[],
	): React.ReactNode[] => {
		const updatedFeatures = [...features];
		const creditPlan = creditPlans[planId];

		if (!creditPlan) return updatedFeatures;

		// 处理IMAGE_TO_TEXT特性
		const imageToTextCredit = creditPlan.credits.find(
			(credit) => credit.type === "IMAGE_TO_TEXT",
		);
		if (imageToTextCredit) {
			const imageToTextIndex = updatedFeatures.findIndex(
				(feature) =>
					typeof feature === "string" &&
					(feature.includes("Image-to-Text") ||
						feature.includes("图像转文本") ||
						feature.includes("图像到文本")),
			);

			if (imageToTextIndex >= 0) {
				// 如果是无限制
				if (imageToTextCredit.amount === Number.POSITIVE_INFINITY) {
					updatedFeatures[imageToTextIndex] = t(
						"pricing.unlimitedImageToText",
					);
				} else {
					updatedFeatures[imageToTextIndex] = t(
						"pricing.imageToTextAmount",
						{
							amount: imageToTextCredit.amount,
							period: imageToTextCredit.period,
						},
					);
				}
			}
		}

		// 处理TEXT_TO_IMAGE特性 (对应Flux.1或Bonus)
		const textToImageCredit = creditPlan.credits.find(
			(credit) => credit.type === "TEXT_TO_IMAGE",
		);
		if (textToImageCredit) {
			// Free计划显示为Flux.1
			if (planId === "free") {
				const fluxIndex = updatedFeatures.findIndex(
					(feature) =>
						typeof feature === "string" &&
						feature.includes("Flux.1"),
				);

				if (fluxIndex >= 0) {
					updatedFeatures[fluxIndex] = t("pricing.fluxGenerations", {
						amount: textToImageCredit.amount,
						period: textToImageCredit.period,
					});
				}
			} else {
				// 付费计划处理Bonus credits - 寻找包含Bonus的特性行
				const bonusIndex = updatedFeatures.findIndex(
					(feature) =>
						typeof feature === "string" &&
						(feature.includes("Bonus") || feature.includes("额外")),
				);

				if (bonusIndex >= 0) {
					// 始终使用TEXT_TO_IMAGE中的数量作为bonus值，不依赖翻译文件中的硬编码值
					const bonusAmount = textToImageCredit.amount;

					// 无论当前语言是什么，都使用来自config/credits.ts的实际值
					updatedFeatures[bonusIndex] = t("pricing.bonusCredits", {
						amount: bonusAmount,
					});
				}
			}
		}

		return updatedFeatures;
	};

	return (
		<div className={cn("@container", className)}>
			{hasSubscriptions && (
				<div className="mb-6 flex @xl:justify-center">
					<Tabs
						value={interval}
						onValueChange={(value) =>
							setInterval(value as typeof interval)
						}
						data-test="price-table-interval-tabs"
					>
						<TabsList className="border-foreground/10">
							<TabsTrigger value="month">
								{t("pricing.monthly")}
							</TabsTrigger>
							<TabsTrigger value="year">
								{t("pricing.yearly")}
							</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
			)}
			<div
				className={cn("grid grid-cols-1 gap-6", {
					"@xl:grid-cols-2": filteredPlans.length >= 2,
					"@4xl:grid-cols-3": filteredPlans.length >= 3,
					"@5xl:grid-cols-4": filteredPlans.length >= 4,
				})}
			>
				{filteredPlans.map(([planId, plan]) => {
					const { isFree, isEnterprise, prices, recommended } = plan;
					const { title, features: originalFeatures } =
						planData[planId as keyof typeof planData];

					// 使用credits.ts数据更新特性
					const features = getUpdatedFeatures(
						planId,
						originalFeatures,
					);

					// 从credits.ts获取价格
					const creditPrice = creditPlans[planId]?.price || 0;
					const creditCurrency =
						creditPlans[planId]?.currency || "USD";

					// 根据当前interval获取对应的产品ID
					const planProductId =
						prices?.find(
							(p) =>
								p.type === "recurring" &&
								p.interval === interval,
						)?.productId || "";

					// 首先使用credits.ts中的价格
					let price: any = null;

					if (isFree) {
						price = {
							amount: 0,
							currency: localeCurrency,
							interval,
							productId: planProductId,
							type: "recurring",
						};
					} else if (creditPrice > 0) {
						// 优先使用credits.ts中的价格，但保留原始productId
						// 年度价格计算：提供2个月免费(月度价格 * 10)
						const amount =
							interval === "year"
								? creditPrice * 10
								: creditPrice;

						price = {
							amount,
							currency: creditCurrency,
							interval,
							productId: planProductId, // 使用配置中的productId
							type: "recurring",
						};
					} else {
						// 如果credits.ts中没有价格，则使用config中的价格
						price = prices?.find(
							(price) =>
								!price.hidden &&
								(price.type === "one-time" ||
									price.interval === interval) &&
								price.currency === localeCurrency,
						);
					}

					if (!(price || isEnterprise)) {
						return null;
					}

					// 检查是否是当前激活的计划，需要同时比较planId和interval
					// 1. 首先确认planId是否匹配
					// 2. 如果是周期性计划（subscription），则必须检查interval是否匹配
					// 3. 只有当前计划ID匹配且interval匹配时才为true
					const isCurrentPlan =
						planId === activePlanId &&
						(isFree ||
							price?.type !== "recurring" ||
							activeInterval === interval);

					return (
						<div
							key={planId}
							className={cn("rounded-xl border bg-card p-7", {
								"border-2 border-primary": recommended,
							})}
							data-test="price-table-plan"
						>
							<div className="flex h-full flex-col justify-between gap-4">
								<div>
									{recommended && (
										<div className="-mt-9 flex justify-center">
											<div className="mb-2 flex h-6 w-auto items-center gap-1.5 rounded-full bg-primary px-2 py-1 font-semibold text-primary-foreground text-xs">
												<StarIcon className="size-3" />
												{t("pricing.recommended")}
											</div>
										</div>
									)}
									<h3
										className={cn(
											"my-0 font-semibold text-2xl",
											{
												"font-bold text-primary":
													recommended,
											},
										)}
									>
										{title}
									</h3>

									{!!features?.length && (
										<ul className="mt-4 grid list-none gap-2 text-sm">
											{features.map((feature, key) => {
												const FeatureIcon =
													getFeatureIcon(
														feature as string,
													);
												return (
													<li
														key={key}
														className="flex items-center justify-start"
													>
														<FeatureIcon className="mr-3 size-5 text-primary flex-shrink-0" />
														<span>{feature}</span>
													</li>
												);
											})}
										</ul>
									)}

									{price &&
										"trialPeriodDays" in price &&
										price.trialPeriodDays && (
											<div className="mt-4 flex items-center justify-start font-medium text-primary text-sm opacity-80">
												<BadgePercentIcon className="mr-2 size-4" />
												{t("pricing.trialPeriod", {
													days: price.trialPeriodDays,
												})}
											</div>
										)}
								</div>

								<div>
									{price && (
										<strong
											className="block font-medium text-2xl lg:text-3xl"
											data-test="price-table-plan-price"
										>
											{Intl.NumberFormat("en-US", {
												style: "currency",
												currency: price.currency,
												minimumFractionDigits: 0,
											}).format(price.amount)}
											{"interval" in price && (
												<span className="font-normal text-xs opacity-60">
													{" / "}
													{interval === "month"
														? t("pricing.month", {
																count:
																	price.intervalCount ??
																	1,
															})
														: t("pricing.year", {
																count:
																	price.intervalCount ??
																	1,
															})}
												</span>
											)}
											{interval === "year" &&
												price.amount > 0 && (
													<span className="block mt-1 font-normal text-xs text-green-500">
														Save{" "}
														{Intl.NumberFormat(
															"en-US",
															{
																style: "currency",
																currency:
																	price.currency,
																minimumFractionDigits: 0,
															},
														).format(
															price.amount *
																0.167,
														)}{" "}
														(2 months free)
													</span>
												)}
											{organizationId &&
												"seatBased" in price &&
												price.seatBased && (
													<span className="font-normal text-xs opacity-60">
														{" / "}
														{t("pricing.perSeat")}
													</span>
												)}
										</strong>
									)}

									{isEnterprise ? (
										<Button
											className="mt-4 w-full"
											variant="outline"
											asChild
										>
											<LocaleLink href="/contact">
												<PhoneIcon className="mr-2 size-4" />
												{t("pricing.contactSales")}
											</LocaleLink>
										</Button>
									) : (
										<>
											{isCurrentPlan ? (
												<Button
													className="mt-4 w-full"
													variant="outline"
													disabled={true}
												>
													{t("pricing.currentPlan")}
												</Button>
											) : (userId || organizationId) &&
											  activePlan?.status === "active" &&
											  price?.productId ? (
												<ChangePlanButton
													userId={userId}
													organizationId={
														organizationId
													}
													productId={price.productId}
													className={cn(
														"mt-4 w-full",
														recommended
															? "bg-primary text-white hover:bg-primary/90"
															: "",
													)}
												/>
											) : (
												<Button
													className="mt-4 w-full"
													variant={
														recommended
															? "primary"
															: "secondary"
													}
													onClick={() =>
														onSelectPlan(
															planId as PlanId,
															price?.productId,
														)
													}
													loading={loading === planId}
													disabled={
														!price?.productId ||
														activePlan?.status ===
															"cancelled"
													}
													title={
														activePlan?.status ===
														"cancelled"
															? "Please resume your subscription before changing plans"
															: ""
													}
												>
													{userId ||
													organizationId ? (
														t("pricing.choosePlan")
													) : (
														<>
															{t(
																"pricing.getStarted",
															)}
															<ArrowRightIcon className="ml-2 size-4" />
														</>
													)}
												</Button>
											)}
										</>
									)}
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
